import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import { describeTrigger } from "../../../../utils/email-automations";

const NUMBER_FILTERS = new Set(["renewalNumber", "attemptNumber"]);

// One piece of the line under a card's title, as text
const tokenText = (token, t, { short } = {}) => {
  const tr = (key, options) => t(`emailAutomations.trigger.${key}`, options);
  switch (token.type) {
    case "setting":
      return tr(`settings.${token.key}`, { count: token.value });
    case "flag":
      return tr(`flags.${token.key}`);
    case "allMemberships":
      return tr("allMemberships");
    case "allBillingFrequencies":
      return tr("allBillingFrequencies");
    case "recheck":
      return tr(short ? "recheckShort" : "recheck");
    case "trialOnly":
      return tr(short ? "trialOnlyShort" : "trialOnly");
    default:
      break;
  }
  if (token.names) return token.names.join(", ");
  if (NUMBER_FILTERS.has(token.field)) {
    return tr(`filterNumbers.${token.field}`, {
      values: token.values.join(", "),
    });
  }
  if (token.field === "startedOnTrial") {
    return tr(`startedOnTrial.${token.values.join("")}`);
  }
  return token.values
    .map((value) => tr(`filterValues.${token.field}.${value}`))
    .join(", ");
};

/**
 * "Membership purchased · 24/7 Monthly, 24/7 28-Day": the name of the trigger and what narrows it.
 * `compact` is the line of a confirmation: no bolt and no "all memberships"; `children` goes last,
 * in the same style as the details. `short` is the wording of a template card ("trial only").
 */
const TriggerSummary = ({ automation, options, compact, short, children }) => {
  const { t } = useTranslation("coaching-portal");
  const parts = describeTrigger(automation, options)
    .filter(
      (token) =>
        !(
          compact &&
          (token.type === "allMemberships" ||
            token.type === "allBillingFrequencies")
        )
    )
    .map((token) => tokenText(token, t, { short }));
  return (
    <span className={compact ? "ea-trigger ea-trigger--compact" : "ea-trigger"}>
      {compact ? null : (
        <SvgIcon
          src="/img/new-icons/bolt.svg"
          useCurrentColor
          classNames="ea-trigger__bolt"
          style={{ backgroundColor: "var(--warning)" }}
          aria-hidden="true"
        />
      )}
      <span>{automation.trigger.name ?? automation.trigger.key}</span>
      {parts.map((part) => (
        <span key={part} className="ea-trigger__detail">
          {" · "}
          {part}
        </span>
      ))}
      {children ? (
        <span className="ea-trigger__detail">
          {" · "}
          {children}
        </span>
      ) : null}
    </span>
  );
};

TriggerSummary.propTypes = {
  automation: PropTypes.object.isRequired,
  options: PropTypes.object,
  compact: PropTypes.bool,
  short: PropTypes.bool,
  children: PropTypes.node,
};

export default TriggerSummary;
